/**
 * @author: lv.chen
 * @date: 2026/5/18
 * @desc: PieChart 饼状图组件演示文件，展示各种配置效果
 */
import React from 'react';
import PieChart from './index';

import type { IPieChartClickParams } from './types';

const mockData: IPieChartData[] = [
  { name: '类别A', value: 40, id: 'a' },
  { name: '类别B', value: 30, id: 'b' },
  { name: '类别C', value: 20, id: 'c' },
  { name: '类别D', value: 10, id: 'd' },
];

const mockDataWithHighlight: IPieChartData[] = [
  { name: '类别A', value: 40, id: 'a', payload: { extra: 'info_a' } },
  { name: '类别B', value: 30, id: 'b', payload: { extra: 'info_b' } },
  { name: '类别C', value: 20, id: 'c', payload: { extra: 'info_c' } },
  { name: '类别D', value: 10, id: 'd', payload: { extra: 'info_d' } },
];

const handlePieClick = (params: IPieChartClickParams): void => {
  console.debug('[PieChart Demo] 扇区点击', params);
};

/**
 * 基础用法演示
 * 传入最简配置，展示默认饼图效果
 */
const BasicDemo: React.FC = () => {
  return (
    <div style={{ width: 420, height: 320 }}>
      <PieChart
        data={mockData}
        loading={false}
        metricName="销量"
        timeRange="2026-01 ~ 2026-05"
      />
    </div>
  );
};

/**
 * 高亮与中心标题演示
 * 展示 highlightId 高亮和 centerTitle 中心标题功能
 */
const HighlightAndTitleDemo: React.FC = () => {
  return (
    <div style={{ width: 420, height: 320 }}>
      <PieChart
        data={mockDataWithHighlight}
        loading={false}
        highlightId="b"
        centerTitle="总计"
        metricName="销量"
      />
    </div>
  );
};

/**
 * 自定义格式化函数演示
 * 展示 valueFormatter 和 labelFormatter 的使用
 */
const CustomFormatterDemo: React.FC = () => {
  const valueFormatter = (value: number | null): string => {
    if (value === null) return '-';
    return `${value}w`;
  };

  return (
    <div style={{ width: 420, height: 320 }}>
      <PieChart
        data={mockData}
        loading={false}
        metricName="销量"
        valueFormatter={valueFormatter}
      />
    </div>
  );
};

/**
 * 扇区点击事件演示
 * 展示 onPieClick 回调的使用
 */
const ClickEventDemo: React.FC = () => {
  return (
    <div style={{ width: 420, height: 320 }}>
      <PieChart
        data={mockData}
        loading={false}
        metricName="销量"
        onPieClick={handlePieClick}
      />
    </div>
  );
};

/**
 * 隐藏标签与图例演示
 * 展示 showLabel 和 showLegend 控制显隐
 */
const HideLabelDemo: React.FC = () => {
  return (
    <div style={{ width: 420, height: 320 }}>
      <PieChart
        data={mockData}
        loading={false}
        showLabel={false}
        showLegend={false}
        centerTitle="总计"
        metricName="销量"
      />
    </div>
  );
};

/**
 * Demo 案例配置
 * 每个配置项对应文档页面中的一个演示场景
 */
export const demoConfig = [
  {
    title: '基础用法',
    description: '传入 data、loading、metricName、timeRange 等基本属性，展示默认饼图效果',
    code: `const data = [
  { name: '类别A', value: 40, id: 'a' },
  { name: '类别B', value: 30, id: 'b' },
  { name: '类别C', value: 20, id: 'c' },
  { name: '类别D', value: 10, id: 'd' },
];

<PieChart
  data={data}
  loading={false}
  metricName="销量"
  timeRange="2026-01 ~ 2026-05"
/>`,
    element: <BasicDemo />,
  },
  {
    title: '高亮与中心标题',
    description: '通过 highlightId 高亮指定扇区，通过 centerTitle 在饼图中心展示标题文本',
    code: `const data = [
  { name: '类别A', value: 40, id: 'a' },
  { name: '类别B', value: 30, id: 'b' },
  { name: '类别C', value: 20, id: 'c' },
  { name: '类别D', value: 10, id: 'd' },
];

<PieChart
  data={data}
  loading={false}
  highlightId="b"
  centerTitle="总计"
  metricName="销量"
/>`,
    element: <HighlightAndTitleDemo />,
  },
  {
    title: '自定义数值格式化',
    description: '通过 valueFormatter 自定义数值的展示格式，如添加单位后缀',
    code: `const valueFormatter = (value: number | null): string => {
  if (value === null) return '-';
  return \`\${value}w\`;
};

<PieChart
  data={data}
  loading={false}
  metricName="销量"
  valueFormatter={valueFormatter}
/>`,
    element: <CustomFormatterDemo />,
  },
  {
    title: '扇区点击事件',
    description: '通过 onPieClick 监听扇区点击，获取名称、值、索引和原始数据',
    code: `const handlePieClick = (params: IPieChartClickParams): void => {
  console.debug('[PieChart] 扇区点击', params);
};

<PieChart
  data={data}
  loading={false}
  metricName="销量"
  onPieClick={handlePieClick}
/>`,
    element: <ClickEventDemo />,
  },
  {
    title: '隐藏标签与图例',
    description: '通过 showLabel 和 showLegend 控制标签、图例的显示与隐藏，配合 centerTitle 使用',
    code: `<PieChart
  data={data}
  loading={false}
  showLabel={false}
  showLegend={false}
  centerTitle="总计"
  metricName="销量"
/>`,
    element: <HideLabelDemo />,
  },
];

export default demoConfig;
